import React, { useState, useEffect } from 'react';
import { X, Shirt, Search, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';
import { assignGarmentTypesToEdificio, getEdificioGarmentTypes } from '@/services/edificioGarmentTypes.service';

const AssignGarmentTypesModal = ({ isOpen, onClose, edificio, onSuccess }) => {
  const { toast } = useToast();
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [selectedGarmentTypes, setSelectedGarmentTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && edificio) {
      loadData();
    }
  }, [isOpen, edificio]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Cargar todos los tipos de prenda activos
      const garmentTypesResponse = await getGarmentTypes({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' }
      });

      setGarmentTypes(garmentTypesResponse.data || []);

      // Cargar tipos de prenda ya asignados al edificio
      const assignedResponse = await getEdificioGarmentTypes(edificio._id || edificio.id);
      setSelectedGarmentTypes(assignedResponse.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los tipos de prenda",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleGarmentType = (garmentTypeId) => {
    setSelectedGarmentTypes(prev => {
      if (prev.includes(garmentTypeId)) {
        return prev.filter(id => id !== garmentTypeId);
      } else {
        return [...prev, garmentTypeId];
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await assignGarmentTypesToEdificio(edificio._id || edificio.id, selectedGarmentTypes);
      
      toast({
        title: "Éxito",
        description: "Tipos de prenda asignados correctamente",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error al asignar tipos de prenda:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudieron asignar los tipos de prenda",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredGarmentTypes = garmentTypes.filter(gt =>
    gt.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shirt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Asignar Tipos de Prenda
              </h2>
              <p className="text-sm text-gray-500">
                {edificio?.nombre}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar tipos de prenda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Selected Count */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedGarmentTypes.length} tipo(s) de prenda seleccionado(s)
            </p>
            {selectedGarmentTypes.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedGarmentTypes([])}
              >
                Limpiar selección
              </Button>
            )}
          </div>

          {/* Garment Types List */}
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Cargando tipos de prenda...</p>
            </div>
          ) : filteredGarmentTypes.length === 0 ? (
            <div className="text-center py-8">
              <Shirt className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No se encontraron tipos de prenda</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredGarmentTypes.map((garmentType) => {
                const isSelected = selectedGarmentTypes.includes(garmentType._id);
                return (
                  <div
                    key={garmentType._id}
                    onClick={() => handleToggleGarmentType(garmentType._id)}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`
                          p-2 rounded-lg
                          ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}
                        `}>
                          <Shirt className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{garmentType.nombre}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {garmentType.peso}g
                            </Badge>
                            {garmentType.descripcion && (
                              <p className="text-xs text-gray-500 truncate">
                                {garmentType.descripcion}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="ml-3">
                          <div className="h-6 w-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssignGarmentTypesModal;
